import { createRouter, createWebHistory } from 'vue-router'
import { LOGGER } from '@/util/logger'
import auth from '@/auth'
import { useUserLogin } from '@/stores/userLogin'
import { useWorkflowInProgressStore } from '@/stores/workflowInProgress'

// Public views
const NewResident = () => import('@/views/public/NewResident.vue')
const HomeView = () => import('@/views/public/HomeView.vue')
const SubmitConfirmationEmail = () => import('@/views/public/contact-us/SubmitConfirmationEmail.vue')
const ContactUsView = () => import('@/views/public/contact-us/ContactUsView.vue')
const MotorVehicleQuestion = () => import('@/views/public/contact-us/MotorVehicleQuestion.vue')
const GeneralQuestions = () => import('@/views/public/contact-us/GeneralQuestions.vue')
const ApportionedRegistration = () => import('@/views/public/contact-us/ApportionedRegistration.vue')
const AboutView = () => import('@/views/public/AboutView.vue')
const SurveyMain = () => import('@/views/public/surveys/SurveyMain.vue')
const FindALocation = () => import('@/views/public/other-services/FindALocation.vue')
const TransactionLoggedOut = () => import('@/views/public/select-service/TransactionLoggedOut.vue')
const SurveyDLSched = () => import('@/views/public/surveys/SurveyDLSched.vue')
const SurveyDVWebsite = () => import('@/views/public/surveys/SurveyDVWebsite.vue')
const SurveyOnlineServices = () => import('@/views/public/surveys/SurveyOnlineServices.vue')
const OrganDonorLogin = () => import('@/views/public/organ-donor/OrganDonorLogin.vue')
const AddContact = () => import('@/views/public/select-service/emergency-contact/AddContact.vue')
const TransactionComplete = () => import('@/views/public/select-service/TransactionComplete.vue')
const CheckoutReceipt = () => import('@/views/public/select-service/CheckoutReceipt.vue')
const ShoppingCart = () => import('@/views/public/select-service/ShoppingCart.vue')
const COAReviewAddressUpdate = () => import('@/views/public/select-service/change-of-address/ReviewAddressUpdate.vue')
const COAVerifyIdentity = () => import('@/views/public/select-service/change-of-address/VerifyIdentity.vue')
const COAChangeAddress = () => import('@/views/public/select-service/change-of-address/ChangeAddress.vue')
const COAAdditionalRecords = () => import('@/views/public/select-service/change-of-address/AdditionalRecords.vue')
const RenewalOptions = () => import('@/views/public/select-service/dl-renew/RenewalOptions.vue')
const DLRealIdOptInVerification = () =>
  import('@/views/public/select-service/dl-real-id-verification/DLRealIDOptInVerification.vue')
const DLRealIdOptInOptions = () =>
  import('@/views/public/select-service/dl-real-id-verification/DLRealIDOptInOptions.vue')
const DuplicateOptions = () => import('@/views/public/select-service/dl-duplicate/DuplicateOption.vue')
const DuplicateDonations = () => import('@/views/public/select-service/dl-duplicate/DuplicateDonations.vue')
const RenewalDonations = () => import('@/views/public/select-service/dl-renew/RenewalDonations.vue')
const RenewalReview = () => import('@/views/public/select-service/dl-renew/RenewalReview.vue')
const VanityPlate = () => import('@/views/public/vanity-plate/VanityPlate.vue')
const DLVerifyInformation = () => import('@/views/public/DLVerifyInformation.vue')
const DLLogin = () => import('@/views/public/DLLogin.vue')
const ESLogin = () => import('@/views/public/exam-scheduling/ESLogin.vue')
const ESDriverTestDashboard = () => import('@/views/public/exam-scheduling/ESDriverTestDashboard.vue')
const ESCancellationComplete = () => import('@/views/public/exam-scheduling/ESCancellationComplete.vue')
const ESScheduleTest = () => import('@/views/public/exam-scheduling/ESScheduleTest.vue')
const ESReschedulingComplete = () => import('@/views/public/exam-scheduling/ESReschedulingComplete.vue')
const PreVerifyLogin = () => import('@/views/public/PreVerifyLogin.vue')
const PreVerificationStep = () => import('@/views/public/PreVerificationStep.vue')
const ODDesignationInformation = () => import('@/views/public/organ-donor/ODDesignationInformation.vue')
const ODTransactionComplete = () => import('@/views/public/organ-donor/ODTransactionComplete.vue')
const ODAlreadyRegistered = () => import('@/views/public/organ-donor/ODAlreadyRegistered.vue')
const VSLogin = () => import('@/views/public/vehicle/VSLogin.vue')
const VSSelectService = () => import('@/views/public/select-service/VSSelectService.vue')
const DLSelectService = () => import('@/views/public/select-service/DLSelectService.vue')
const VSVerifyInformation = () => import('@/views/public/vehicle/VSVerifyInformation.vue')
const AddInsuranceInfo = () => import('@/views/public/vehicle/AddInsuranceInfo.vue')
const VRRenew = () => import('@/views/public/select-service/vr-renew/VRRenew.vue')
const VRDuplicateReprintOption = () =>
  import('@/views/public/select-service/vr-duplicate/VRDuplicateReprintOption.vue')
const VRDuplicateReview = () => import('@/views/public/select-service/vr-duplicate/VRDuplicateReview.vue')
const RetiredRegistration = () => import('@/views/public/vehicle/RetiredRegistration.vue')
const VRVerifyIdentity = () => import('@/views/public/vehicle/VRVerifyIdentity.vue')
const VRCOAVerifyIdentity = () => import('@/views/public/vehicle/VRCOAVerifyIdentity.vue')
const VRAdditionalRecords = () => import('@/views/public/vehicle/VRAdditionalRecords.vue')
const VRRenewRR = () => import('@/views/public/vehicle/VRRenewRR.vue')

// Admin views
const DLDashboard = () => import('@/views/admin/dl-dashboard/DLDashboard.vue')
const VRDashboard = () => import('@/views/admin/vr-dashboard/VRDashboard.vue')
const ODDashboard = () => import('@/views/admin/od-dashboard/ODDashboard.vue')
const FADashboard = () => import('@/views/admin/fa-dashboard/FADashboard.vue')
const ESDashboard = () => import('@/views/admin/es-dashboard/ESDashboard.vue')
const CallbackPage = () => import('@/views/admin/CallbackPage.vue')
const LogoutPage = () => import('@/views/admin/LogoutPage.vue')
const TransactionDetails = () => import('@/views/admin/dl-dashboard/TransactionDetails.vue')
const AdvancedSearch = () => import('@/views/admin/advancedSearch/AdvancedSearch.vue')

const APP_TITLE = 'EGov'
const ADMIN_TITLE = 'EGov Admin'

const redirectWithBucket = (targetRouteName, bucketLabel, date) => ({
  name: targetRouteName,
  query: { bucket: bucketLabel, date },
})

const dashboardRoutes = [
  {
    path: '/admin/dl-dashboard',
    name: 'DLDashboard',
    component: DLDashboard,
    meta: { title: "Driver's License Dashboard" },
  },
  {
    path: '/admin/vr-dashboard',
    name: 'VRDashboard',
    component: VRDashboard,
    meta: { title: 'Vehicle Registration Dashboard' },
  },
  {
    path: '/admin/od-dashboard',
    name: 'ODDashboard',
    component: ODDashboard,
    meta: { title: 'Organ Donor Dashboard' },
  },
  {
    path: '/admin/fa-dashboard',
    name: 'FADashboard',
    component: FADashboard,
    meta: { title: 'Fee Accounting Dashboard' },
  },
  {
    path: '/admin/es-dashboard',
    name: 'ESDashboard',
    component: ESDashboard,
    meta: { title: 'Exam Scheduling Dashboard' },
  },
  {
    path: '/admin/advanced-search',
    name: 'AdvancedSearch',
    component: AdvancedSearch,
    meta: { title: 'Advanced Search' },
  },
].map((route) => ({ ...route, meta: { ...route.meta, requiresAuth: true } }))

const adminRedirectRoutes = dashboardRoutes.map(({ name }) => ({
  path: `/admin/${name.toLowerCase()}-redirect`,
  redirect: (to) => redirectWithBucket(name, to.query.bucketLabel, to.query.date),
  meta: { requiresAuth: true },
}))

const adminRoutes = [
  { path: '/admin/callback', name: 'Callback', component: CallbackPage, meta: { requiresAuth: false } },
  { path: '/admin/', redirect: { name: 'Callback' }, meta: { requiresAuth: true } },
  { path: '/admin', redirect: { name: 'Callback' }, meta: { requiresAuth: true } },
  { path: '/logout', name: 'LogoutPage', component: LogoutPage, meta: { title: 'Logged Out' } },
  {
    path: '/admin/transaction/:transactionId',
    name: 'TransactionDetails',
    component: TransactionDetails,
    meta: { requiresAuth: true, title: 'Transaction Details' },
    props: true,
  },
  ...dashboardRoutes,
  ...adminRedirectRoutes,
  // FIX: catch-all for unmatched admin routes
  { path: '/:pathMatch(.*)*', redirect: { name: 'Callback' } },
]

const requiresDLSession = () => {
  const userLogin = useUserLogin()
  const dlNumber = userLogin.currentDlNumber
  if (!dlNumber || !userLogin.getLoginResponse(dlNumber)) {
    return { name: 'DLLogin' }
  }
}

const requiresRenewalSession = () => {
  const userLogin = useUserLogin()
  const workflowStore = useWorkflowInProgressStore()
  if (workflowStore.activeServiceType === 'VRRENEW') {
    return userLogin.vsLoginResponse ? undefined : { name: 'VSLogin' }
  }
  return requiresDLSession()
}


const requiresCOASession = () => {
  const userLogin = useUserLogin()
  const workflowStore = useWorkflowInProgressStore()
  if (workflowStore.activeServiceType === 'VRCOA') {
    return userLogin.vsLoginResponse ? undefined : { name: 'VSLogin' }
  }
  return requiresDLSession()
}

const requiresNavFrom = (fallbackRouteName) => (to, from) => {
  if (!from.name) return { name: fallbackRouteName }
}

const publicRoutes = [
  { path: '/', redirect: '/public/home' },
  { path: '/public/home', name: 'Home', component: HomeView, meta: { title: 'Home' } },
  { path: '/public/about', name: 'About', component: AboutView, meta: { title: 'About' } },

  // Contact
  { path: '/public/contact-us', redirect: '/public/contact-us/dvs' },
  { path: '/public/contact-us/dvs', name: 'ContactUsDVS', component: ContactUsView, meta: { title: 'Contact Us' } },
  {
    path: '/public/contact-us/sce',
    name: 'ContactUsConfirm',
    component: SubmitConfirmationEmail,
    meta: { title: 'Submit Confirmation Email' },
  },
  {
    path: '/public/contact-us/arq',
    name: 'ContactUsARQ',
    component: ApportionedRegistration,
    meta: { title: 'Apportioned Registration' },
  },
  { path: '/public/contact-us/ofs', name: 'ContactUsOFS', component: NewResident, meta: { title: 'New Resident' } },
  {
    path: '/public/contact-us/mvq',
    name: 'ContactUsMVQ',
    component: MotorVehicleQuestion,
    meta: { title: 'Motor Vehicle Question' },
  },
  {
    path: '/public/contact-us/general-questions',
    name: 'ContactUsGeneralQuestions',
    component: GeneralQuestions,
    meta: { title: 'General Questions' },
  },
  // Find a location
  {
    path: '/public/find-a-location',
    name: 'FindALocation',
    component: FindALocation,
    meta: { title: 'Find A Location' },
  },

  // Surveys
  { path: '/public/surveys', name: 'Surveys', component: SurveyMain, meta: { title: 'Surveys' } },
  { path: '/public/logout', name: 'DLLogout', component: TransactionLoggedOut, meta: { title: 'Logged Out' } },
  {
    path: '/public/surveys/drivers-test-scheduling',
    name: 'DriversTestSchedulingSurvey',
    component: SurveyDLSched,
    meta: { title: "Driver's Test Scheduling Survey" },
  },
  {
    path: '/public/surveys/drivers-vehicle-website',
    name: 'DriversVehicleWebsiteSurvey',
    component: SurveyDVWebsite,
    meta: { title: "Driver's Vehicle Website Survey" },
  },
  {
    path: '/public/surveys/online-services',
    name: 'OnlineServiesSurvey',
    component: SurveyOnlineServices,
    meta: { title: 'Online Services Survey' },
  },

  // Services
  { path: '/public/select-service', redirect: '/public/dl-select-service' },
  {
    path: '/public/dl-select-service',
    name: 'SelectService',
    component: DLSelectService,
    beforeEnter: requiresDLSession,
    meta: { title: 'Select Service', requiresNav: true },
  },
  {
    path: '/public/shopping-cart',
    name: 'ShoppingCart',
    component: ShoppingCart,
    meta: { title: 'Shopping Cart' },
  },
  {
    path: '/public/transaction-complete',
    name: 'TransactionComplete',
    component: TransactionComplete,
    meta: { title: 'Transaction Complete', requiresNav: true },
  },
  {
    path: '/public/receipt',
    name: 'CheckoutReceipt',
    component: CheckoutReceipt,
    meta: { title: 'Payment receipt' },
  },
  { path: '/public/vanity-plate', name: 'VanityPlate', component: VanityPlate, meta: { title: 'Vanity Plate' } },
  {
    path: '/public/emergency-contact/add-emergency-contact',
    name: 'AddEmergencyContact',
    component: AddContact,
    meta: { title: 'Add Emergency Contact', requiresNav: true },
  },

  // Driver's license
  { path: '/public/dl-login', name: 'DLLogin', component: DLLogin, meta: { title: "Driver's License Login" } },
  {
    path: '/public/dl-verify-information',
    name: 'DLVerifyInformation',
    component: DLVerifyInformation,
    beforeEnter: requiresDLSession,
    meta: { title: 'Verify Information', requiresNav: true },
  },
  {
    path: '/public/pre-verify-login',
    name: 'PreVerifyLogin',
    component: PreVerifyLogin,
    meta: { title: 'Pre-Verify Login' },
  },
  {
    path: '/public/pre-verification-step',
    name: 'PreVerificationStep',
    component: PreVerificationStep,
    beforeEnter: requiresNavFrom('PreVerifyLogin'),
    meta: { title: 'Pre-Verification Step', requiresNav: true },
  },
  {
    path: '/public/real-id-opt-in-verification/:serviceType',
    name: 'DLRealIdOptInVerification',
    component: DLRealIdOptInVerification,
    beforeEnter: requiresDLSession,
    meta: { title: 'Real Id Opt In Verification', requiresNav: true },
  },
  {
    path: '/public/dl-rid-opt-in/options',
    name: 'DLRealIdOptInOptions',
    component: DLRealIdOptInOptions,
    beforeEnter: requiresDLSession,
    meta: { title: 'Real Id Opt In Options', requiresNav: true },
  },

  // DL renewal workflow - requiresNav prevents direct URL access
  {
    path: '/public/dl-renew/renewal-options/:serviceType',
    name: 'RenewalOptions',
    component: RenewalOptions,
    beforeEnter: requiresDLSession,
    meta: { title: 'Renewal Options', requiresNav: true },
  },
  {
    path: '/public/donations',
    name: 'RenewalDonations',
    component: RenewalDonations,
    beforeEnter: requiresRenewalSession,
    meta: { title: 'Renewal - Donations', requiresNav: true },
  },
  {
    path: '/public/review-service',
    name: 'RenewalReview',
    component: RenewalReview,
    beforeEnter: requiresRenewalSession,
    meta: { title: 'Renewal - Review', requiresNav: true },
  },

  {
    path: '/public/dl-duplicate/duplicate-options/:serviceType',
    name: 'DuplicateOptions',
    component: DuplicateOptions,
    meta: { title: 'Duplicate - Options', requiresNav: true },
  },

  {
    path: '/public/dl-duplicate/duplicate-donations',
    name: 'DuplicateDonations',
    component: DuplicateDonations,
    beforeEnter: requiresDLSession,
    meta: { title: 'Duplicate - Donations', requiresNav: true },
  },

  // Change of address workflow - requiresNav prevents direct URL access
  {
    path: '/public/change-of-address/verify-identity',
    name: 'COAVerifyIdentity',
    component: COAVerifyIdentity,
    //beforeEnter: requiresDLSession,
    meta: { title: 'Change of Address - Verify Identity', requiresNav: true },
  },
  {
    path: '/public/change-of-address/change-address',
    name: 'COAChangeAddress',
    component: COAChangeAddress,
    beforeEnter: requiresCOASession,
    meta: { title: 'Change of Address - Change Address', requiresNav: true },
  },
  {
    path: '/public/change-of-address/additional-records',
    name: 'COAAdditionalRecords',
    component: COAAdditionalRecords,
    beforeEnter: requiresDLSession,
    meta: { title: 'Change of Address - Additional Records', requiresNav: true },
  },
  {
    path: '/public/change-of-address/review-address-update',
    name: 'COAReviewAddressUpdate',
    component: COAReviewAddressUpdate,
    beforeEnter: requiresCOASession,
    meta: { title: 'Change of Address - Review Update', requiresNav: true },
  },

  // Organ donor
  {
    path: '/public/organ-donor-login',
    name: 'OrganDonorLogin',
    component: OrganDonorLogin,
    meta: { title: 'Organ Donor Login' },
  },
  {
    path: '/public/od-designation-information',
    name: 'ODDesignationInformation',
    component: ODDesignationInformation,
    beforeEnter: requiresNavFrom('OrganDonorLogin'),
    meta: { title: 'Organ Donor Designation Information', requiresNav: true },
  },
  {
    path: '/public/od-complete',
    name: 'ODTransactionComplete',
    component: ODTransactionComplete,
    beforeEnter: requiresNavFrom('OrganDonorLogin'),
    meta: { title: 'Organ Donor Transaction Complete', requiresNav: true },
  },
  {
    path: '/public/od-already-registered',
    name: 'ODAlreadyRegistered',
    component: ODAlreadyRegistered,
    beforeEnter: requiresNavFrom('OrganDonorLogin'),
    meta: { title: 'Organ Donor Already Registered', requiresNav: true },
  },

  // Exam scheduling login
  {
    path: '/public/es-login',
    name: 'ESLogin',
    component: ESLogin,
    meta: { title: 'Exam Scheduling Login' },
  },
  {
    path: '/public/es-driver-cancellation-complete',
    name: 'ESCancellationComplete',
    component: ESCancellationComplete,
    beforeEnter: requiresNavFrom('ESLogin'),
    meta: { title: 'Driver Cancellation Complete', requiresNav: true },
  },
  {
    path: '/public/es-driver-test-dashboard',
    name: 'ESDriverTestDashboard',
    component: ESDriverTestDashboard,
    beforeEnter: requiresNavFrom('ESLogin'),
    meta: { title: 'Driver Test Dashboard', requiresNav: true },
  },
  {
    path: '/public/es-schedule-test/:serviceType',
    name: 'ESScheduleTest',
    component: ESScheduleTest,
    beforeEnter: requiresNavFrom('ESLogin'),
    props: true,
    meta: { title: 'Schedule Test', requiresNav: true },
  },
  {
    path: '/public/es-rescheduling-complete/',
    name: 'ESReschedulingComplete',
    component: ESReschedulingComplete,
    beforeEnter: requiresNavFrom('ESLogin'),
    props: true,
    meta: { title: 'Schedule Test', requiresNav: true },
  },
  //Vehicle Services
  { path: '/public/vs-login', name: 'VSLogin', component: VSLogin, meta: { title: 'Vehicle Services Login' } },
  {
    path: '/public/vs-select-service',
    name: 'VSSelectService',
    component: VSSelectService,
    beforeEnter: requiresNavFrom('VSLogin'),
    meta: { title: 'VS Select a Service' },
  },
  {
    path: '/public/vs-verify-information',
    name: 'VSVerifyInformation',
    component: VSVerifyInformation,
    beforeEnter: requiresNavFrom('VSLogin'),
    meta: { title: 'Verify Information' },
  },
  {
    path: '/public/vs-add-insurance-info',
    name: 'AddInsuranceInfo',
    component: AddInsuranceInfo,
    beforeEnter: requiresNavFrom('VSLogin'),
    meta: { title: 'Add Insurance Information' },
  },
  {
    path: '/public/vr-renew',
    name: 'VRRenew',
    component: VRRenew,
    beforeEnter: requiresNavFrom('VSSelectService'),
    meta: { title: 'Vehicle Registration Renewal', requiresNav: true },
  },
  {
    path: '/public/vr-duplicate/options',
    name: 'VRDuplicateReprintOption',
    component: VRDuplicateReprintOption,
    beforeEnter: requiresNavFrom('VSSelectService'),
    meta: { title: 'Duplicate/Reprint Options', requiresNav: true },
  },
  {
    path: '/public/vr-duplicate/review',
    name: 'VRDuplicateReview',
    component: VRDuplicateReview,
    beforeEnter: requiresNavFrom('VSSelectService'),
    meta: { title: 'Review Duplicate/Reprint', requiresNav: true },
  },
  {
    path: '/public/vr-renew/donations',
    name: 'VRRenewDonations',
    component: RenewalDonations,
    beforeEnter: requiresNavFrom('VRRenew'),
    meta: { title: 'Vehicle Registration Renewal - Donations', requiresNav: true },
  },
  {
    path: '/public/vr-retired-registration',
    name: 'RetiredRegistration',
    component: RetiredRegistration,
    //beforeEnter: requiresNavFrom('VSLogin'),
    meta: { title: 'Retired Registration' },
  },
  {
    path: '/public/vr-verify-identity',
    name: 'VRVerifyIdentity',
    component: VRVerifyIdentity,
    beforeEnter: requiresNavFrom('VSSelectService'),
    meta: { title: 'Verify Your Identity' },
  },
  {
    path: '/public/vr-change-of-address/verify-identity',
    name: 'VRCOAVerifyIdentity',
    component: VRCOAVerifyIdentity,
    beforeEnter: requiresNavFrom('VSSelectService'),
    meta: { title: 'Change of Address - Verify Your Identity', requiresNav: true },
  },
  {
    path: '/public/vr-change-of-address/additional-records',
    name: 'VRAdditionalRecords',
    component: VRAdditionalRecords,
    beforeEnter: requiresCOASession,
    meta: { title: 'Change of Address - Additional Records', requiresNav: true },
  },
  {
    path: '/public/vr-renew-retired',
    name: 'VRRenewRR',
    component: VRRenewRR,
    beforeEnter: requiresNavFrom('VSSelectService'),
    meta: { title: 'Vehicle Registration Renewal', requiresNav: true },
  },


  { path: '/:pathMatch(.*)*', redirect: '/public/home' },
]

const setupAuthGuard = (router) => {
  router.beforeEach(async (to) => {
    LOGGER.logEvent(to?.fullPath, 'Navigation')

    if (!to?.meta?.requiresAuth) return true
    if (to?.name === 'Callback') return true

    const user = await auth.getUser()
    if (user && !user?.expired) return true

    LOGGER.logEvent('User is not authenticated, triggering login')
    await auth.login()
    return false
  })
}

const setupTitleGuard = (router, appTitle) => {
  router.afterEach((to) => {
    document.title = to?.meta?.title ? `${to.meta.title} | ${appTitle}` : appTitle
  })
}

// Redirects to home if a workflow route is accessed via direct URL rather than in-app navigation
const setupNavGuard = (router) => {
  router.beforeEach((to, from) => {
    if (to?.meta?.requiresNav && from?.name === null) return { name: 'Home' }
  })
}

const scrollToTop = () => ({ top: 0 })

const createPublicRouter = () => {
  const router = createRouter({ history: createWebHistory(), routes: publicRoutes, scrollBehavior: scrollToTop })
  setupNavGuard(router)
  setupTitleGuard(router, APP_TITLE)
  return router
}

const createAdminRouter = () => {
  const router = createRouter({ history: createWebHistory(), routes: adminRoutes, scrollBehavior: scrollToTop })
  setupAuthGuard(router)
  setupTitleGuard(router, ADMIN_TITLE)
  return router
}

let router = null
export function getRouter() {
  if (router) return router
  const path = globalThis.location.pathname
  router = path.startsWith('/admin') ? createAdminRouter() : createPublicRouter()
  return router
}
